<template>
  <forgerock-template id="login-page" container-class="container login-container">
    <bootstrap-alerts :alerts="alerts"/>
    <stihl-section>
      <stihl-overlay :active="state === 'loading'">

        <form @submit.prevent="loginWith(LOGIN_METHOD.LOCAL_AUTH)" class="login-frame login-page">

          <div id="content" class="login-content">

            <div class="login-header">
              <h1 class="title">{{ $t('SUI.page.login.title') }}</h1>
              <div id="little-rect"></div>
            </div>

            <group-input id="username"
                         type="email"
                         :label="$t('SUI.label.email')"
                         group-class="form-group"
                         :required="true"
                         autocomplete="username"
                         autofocus
                         v-model="v$.form.username.$model"
                         :validation-errors="v$.form.username.$errors"
                         @keyup.enter="loginWith(LOGIN_METHOD.LOCAL_AUTH)"/>

            <group-input id="password"
                         type="password"
                         :label="$t('SUI.label.password')"
                         group-class="form-group"
                         :required="true"
                         autocomplete="current-password"
                         v-model="v$.form.password.$model"
                         :validation-errors="v$.form.password.$errors"
                         @keyup.enter="loginWith(LOGIN_METHOD.LOCAL_AUTH)">
              <template v-slot:after>
                  <span class="float-end pw-reset-link">
                    <a id="btnPwdReset" href="/user/ui/reset-password"
                       class="btn-link stihl-link">{{ $t('SUI.page.login.btnPwdReset') }}</a>
                </span>
              </template>
            </group-input>

            <div class="buttons">
              <button id="login" type="button" class="btn btn-primary"
                      @click="loginWith(LOGIN_METHOD.LOCAL_AUTH)" :disabled="state==='loading'">
                {{ $t('SUI.button.login') }}
              </button>

              <button id="passkey" type="button" class="btn btn-secondary"
                      @click="loginWith(LOGIN_METHOD.PASSKEY)" :disabled="state==='loading'">
                {{ $t('SUI.button.loginWithPasskey') }}
              </button>
            </div>

            <div class="finlRegGrp">
              <label>{{ $t('SUI.page.login.newHere') }}</label>
              <a id="btnFinReg" href="/user/ui/reset-password"
                 class="btn-link stihl-link">{{ $t('SUI.page.login.completeRegistration') }}</a>
            </div>
          </div>

          <div class="login-sep"></div>

          <div id="content2" class="login-side">
            <div class="space"></div>
            <div id="USLogin">
              <label class="form-label">Are you from the USA?</label>
              <button id="btnUSLogin" class="btn btn-secondary btn-external" @click="loginWith(LOGIN_METHOD.US_ACCOUNT)" type="button">
                <span>Login with USA account</span> <i class="bi bi-box-arrow-up-right m-1"></i>
              </button>
            </div>
            <div id="EmployeeLogin" v-show="showLoginAsEmployee">
              <label class="form-label">{{ $t('SUI.page.login.lblStihlEmployee') }}</label>
              <button id="btnEmployeeLogin" class="btn btn-secondary btn-external" @click="loginWith(LOGIN_METHOD.MS_ENTRA)" type="button">
                <span>{{ $t('SUI.page.login.btnEmployeeLogin') }}</span> <i class="bi bi-box-arrow-up-right m-1"></i>
              </button>
            </div>
            <div class="space"></div>
            <div id="btnHelp" class="dropdown">
              <label class="form-label">{{ $t('SUI.label.needHelp') }}</label>
              <a id="help" class="btn btn-default btn-external mt-0" :href="$t('SUI.link.contact')" target="_blank">
                {{ $t('SUI.label.contact') }}
                <i class="bi bi-box-arrow-up-right m-1"></i>
              </a>
            </div>
          </div>
        </form>

      </stihl-overlay>
    </stihl-section>
  </forgerock-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import StihlSection from '@/components/ui/StihlSection.vue';
import GroupInput from '@/components/ui/GroupInput.vue';
import BootstrapAlerts, { Alert } from '@/components/ui/BootstrapAlerts.vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@/components/validators';
import ForgerockTemplate from '@/components/layout/ForgerockTemplate.vue';
import StihlOverlay from '@/components/ui/StihlOverlay.vue';
import HelpMenu from '@/components/ui/HelpMenu.vue';
import { fr } from '@/components/forgerock';
import { mapState } from 'pinia';
import { useLoginStore } from '@/components/login.store';
import { CallbackType, FRLoginFailure } from '@forgerock/javascript-sdk';

enum LOGIN_METHOD {
  LOCAL_AUTH = 0,
  US_ACCOUNT = 1,
  MS_ENTRA = 2,
  PASSKEY = 3,
}

export default defineComponent({
  name: 'LoginPage',
  components: { HelpMenu, ForgerockTemplate, StihlSection, GroupInput, BootstrapAlerts, StihlOverlay },
  data() {
    return {
      form: {
        username: '',
        password: '',
      },
      state: 'ready' as 'ready' | 'loading' | 'error',
      alerts: [] as Alert[],
      LOGIN_METHOD: LOGIN_METHOD,
    };
  },
  computed: {
    ...mapState(useLoginStore, ['currentStep', 'goto', 'loginHint', 'locale']),
    showLoginAsEmployee: () => (window.APP_CONFIG.feat.employee?.enabled ?? false),
  },
  methods: {
    alert(message: string, _class?: string) {
      this.alerts.push({ message, class: _class });
    },


    loginWith(method: LOGIN_METHOD): any {
      if (this.state === 'loading') {
        console.debug('wait. loading');
        return;
      }

      if (method === LOGIN_METHOD.LOCAL_AUTH) {
        // trigger validation
        (this.v$ as any).form.$touch();
        if (this.v$.$invalid) return console.error('validation failed', this.v$.$errors);
      } else {
        console.info('redirecting to {}', method.toString());
      }

      this.state = 'loading';
      this.alerts = [];

      // must put some dummy values to pass validation
      const required = (val: string) => (method === LOGIN_METHOD.LOCAL_AUTH ? val : '-');

      this.currentStep.getCallbackOfType(CallbackType.ChoiceCallback).setInputValue(method.valueOf());
      this.currentStep.getCallbackOfType(CallbackType.NameCallback).setInputValue(required(this.form.username));
      this.currentStep.getCallbackOfType(CallbackType.PasswordCallback).setInputValue(required(this.form.password));

      fr.submitStep(this.currentStep, undefined, this.customErrorHandler);
    },

    customErrorHandler(r: FRLoginFailure) {
      this.state = 'error';
      this.alert(r.getMessage() ?? this.$t('SUI.error.generic'));
    }
  },
  mounted() {
    //console.debug(`[SUI]login.init STORE: `, this.$store.state.step)
    if (this.loginHint) this.form.username = this.loginHint;
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        // must match the fields in 'this.form'
        username: { required, },
        password: { required, }
      }
    };
  },
});
</script>

<style scoped>
</style>
